import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useMe from '@/hooks/useMe';
import axiosClient from '@/utils/axiosClient';
import Section from '../Section';

const formSchema = z.object({
  first_name: z.string().min(1, { message: 'Name is required' }),
  last_name: z.string().min(1, { message: 'Name is required' }),
  email: z.string().min(1, { message: 'Email is required' }),
});

const Settings = () => {
  const currentUser = useMe();
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
    },
  });

  useEffect(() => {
    if (currentUser && currentUser.data) {
      const formValues = {
        first_name: currentUser.data.first_name || '',
        last_name: currentUser.data.last_name || '',
        email: currentUser.data.email || '',
      };
      form.reset(formValues as any);
    }
  }, [currentUser.data]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { handleSubmit } = form;

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsSubmitting(true);

    try {
      const response = await axiosClient.put(`v1/users/${currentUser?.data?.id}`, {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
      });
      if (response.status === 200) {
        queryClient.clear();
        window.location.reload();
      } else {
        setIsSubmitting(false);
      }
    } catch (error) {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <div>
        <div className="flex flex-col justify-between gap-4 mb-8 md:flex-row sm:gap-8">
          <div className="flex flex-col">
            <div className="text-3xl font-bold leading-6 text-neutral-800 sm:text-2xl">User Settings</div>
            <div className="mt-2 font-normal leading-normal text-gray-500 text-md md:text-sm">
              Change your user settings here.
            </div>
          </div>
        </div>
      </div>
      <Section title="Account Information">
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-4">
            <div className="flex flex-col">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <div className="w-1/4">
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="What's your first name?" autoComplete="off" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </div>
                )}
              />
              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <div className="w-1/4">
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="What's your last name?" autoComplete="off" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </div>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <div className="w-1/4">
                    <FormItem>
                      <FormLabel>email</FormLabel>
                      <FormControl>
                        <Input placeholder="What is your email address?" autoComplete="off" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </div>
                )}
              />
            </div>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Update Account'}
            </Button>
          </form>
        </Form>
      </Section>
    </>
  );
};

export default Settings;
